import styled, { css } from 'styled-components';
import { rgba, rem, isLight, darken } from '@helpers/style';

const makeFillBtn = (bg) => {
	return css`
		${(p) =>
			p.$link
				? css`
						background: none;
						color: ${p.theme.color[bg]};

						&:hover {
							color: ${darken(p.theme.color[bg], 20)};
						}
				  `
				: css`
						background-color: ${p.theme.color[bg]};
						color: ${isLight(p.theme.color[bg]) ? '#333' : 'white'};

						${p.$selected &&
						css`
							color: ${isLight(p.theme.color[bg]) ? '#333' : 'white'};

							background: ${darken(p.theme.color[bg], 5)};
							border-color: ${darken(p.theme.color[bg], 5)};
						`}

						${!p.$noHover &&
						!p.$selected &&
						!p.$disabled &&
						css`
							&:hover {
								background: ${darken(p.theme.color[bg], 5)};
							}

							&:active {
								background: ${darken(p.theme.color[bg], 10)};
								transform: scale(0.98);
							}
						`}
				  `}

		${(p) =>
			p.$disabled &&
			css`
				cursor: default;
				opacity: 0.6;
			`}
	`;
};

const makeOutlineBtn = (border) => {
	return css`
		${(p) =>
			p.$link
				? css`
						background: none;
						border: none;
						color: ${p.theme.color[border]};

						&:hover {
							color: ${darken(p.theme.color[border], 10)};
						}
				  `
				: css`
						background: none;
						border: 1px solid ${p.theme.color[border]};
						color: ${p.theme.color[border]};

						${p.$selected &&
						css`
							color: ${p.theme.color.white};
							border-color: ${p.theme.color[border]};
							background-color: ${p.theme.color[border]};
						`}

						${!p.$noHover &&
						!p.$disabled &&
						!p.$selected &&
						css`
							&:hover {
								color: ${isLight(p.theme.color[border]) ? '#333' : 'white'};

								border-color: ${p.theme.color[border]};
								background-color: ${p.theme.color[border]};
							}

							&:active {
								border-color: ${rgba(p.theme.color[border], 0.6)};
								transform: scale(0.95);
							}
						`}
				  `}

		${(p) =>
			p.$disabled &&
			css`
				cursor: default;
				border-color: ${p.theme.color.grayMedium};
				color: ${p.theme.color.grayMedium};
				transform: scale(1);
			`}
	`;
};

const scale = {
	lg: css`
		padding: 22px 45px;
		font-size: ${(p) => rem(20, p.theme.fontSize.base)};
		${(p) =>
			p.$loading &&
			css`
				padding: 20px 45px;
			`}
	`,
	md: css`
		padding: 16px 25px;
		font-size: ${(p) => rem(16, p.theme.fontSize.base)};
		${(p) =>
			p.$loading &&
			css`
				padding: 14px 25px;
			`}
	`,
	sm: css`
		font-size: ${(p) => rem(14, p.theme.fontSize.base)};
		padding: 10px 20px;
		${(p) =>
			p.$loading &&
			css`
				padding: 8px 20px;
			`}
	`,
};

const iconScale = {
	lg: css`
		font-size: ${(p) => rem(20, p.theme.fontSize.base)};
	`,
	sm: css`
		font-size: ${(p) => rem(14, p.theme.fontSize.base)};
	`,
};

const spanScale = {
	lg: 10,
	sm: 5,
};

const block = css`
	display: block;
	width: 100%;
`;

const commonStyles = css`
	display: inline-flex;
	text-align: center;
	text-decoration: none;
	direction: rtl;
	outline: 0;
	padding: 0;
	line-height: 1;
	background-repeat: no-repeat;
	background-position: center center;
	position: relative;
	white-space: nowrap;
	overflow: hidden;
	user-select: none;
	cursor: pointer;
	border: none;

	transition: all 0.25s ${(p) => p.theme.transition.custom};
	border-radius: ${(p) => p.theme.button.radius}px;
	color: ${(p) => p.theme.color.white};

	.icon {
		font-size: ${(p) => rem(14, p.theme.fontSize.base)};
		${(p) => p.$size && iconScale[p.$size]}
	}

	span {
		display: block;
		width: 100%;

		${(p) => p.$size && scale[p.$size]}
		&.mr {
			margin-right: ${(p) => spanScale[p.$size] || 8}px;
		}

		&.ml {
			margin-left: ${(p) => spanScale[p.$size] || 8}px;
		}
	}

	${(p) => (p.$outline ? makeOutlineBtn(p.$color) : makeFillBtn(p.$color))}

	${(p) => p.$block && block}
`;

export const StyledButton = styled.button.attrs((p) => ({
	$color: p.$color || 'primary',
	$size: p.$size || 'md',
}))`
	${commonStyles}
`;

export const StyledLink = styled.span.attrs((p) => ({
	$color: p.$color || 'primary',
	$size: p.$size || 'md',
}))`
	a {
		${commonStyles}
	}
`;
